// React binding for the Amplitude flags.
//
// Generic on purpose: this is the first flag we ship, but the deployment is
// meant to carry others, so components ask for a flag key and get its variant
// value back rather than each feature growing its own hook.

import {useEffect, useRef, useState} from "react";

import {DEFAULT_FLAG_VALUE, isEnabledValue} from "./flags";
import {flagValue, loadFlags} from "./experiment";

/**
 * The variant value of `flagKey` for the current learner.
 *
 * @param flagKey  a key from FLAGS
 * @param userId   the learner's email or phone (their `customer_id`) -- the id
 *                 the flag's targeting rules are evaluated against. Undefined
 *                 while signed out, which evaluates the rollout anonymously.
 * @param fallback value to report until the variants arrive, and if they never
 *                 do. Defaults to off.
 * @returns {{value: string, isReady: boolean}} `isReady` distinguishes "the
 *          service says off" from "we have not heard back yet", so a caller
 *          that wants to avoid a flash can wait.
 */
export const useFeatureFlag = (
  flagKey,
  {userId, fallback = DEFAULT_FLAG_VALUE} = {},
) => {
  const [state, setState] = useState({value: fallback, isReady: false});
  const identityRef = useRef(userId);

  useEffect(() => {
    let active = true;

    // A different learner. Their evaluation has not come back yet, so the
    // value on screen belongs to the previous one -- drop it rather than let
    // it stand: whoever just signed in may be someone this flag is off for.
    if (identityRef.current !== userId) {
      identityRef.current = userId;
      setState({value: fallback, isReady: false});
    }

    loadFlags(userId).then(() => {
      // The component may have unmounted, or the user id changed, while the
      // fetch was in flight -- a late resolve must not overwrite newer state.
      if (active)
        setState({value: flagValue(flagKey, fallback), isReady: true});
    });
    return () => {
      active = false;
    };
  }, [flagKey, userId, fallback]);

  return state;
};

/**
 * Convenience wrapper for the common "is this feature on?" question.
 *
 * Accepts either Amplitude shape -- explicit show / no-show variants, or a
 * single "on" variant gated by the rollout -- so the UI does not have to be
 * re-released if the flag is configured the other way. See isEnabledValue.
 */
export const useFeatureEnabled = (flagKey, {userId} = {}) => {
  const {value, isReady} = useFeatureFlag(flagKey, {userId});
  return {enabled: isEnabledValue(value), isReady};
};
